import { useEffect, useState, useCallback } from "react";
import { listVersions, restoreVersion } from "../api";
import "./VersionHistory.css";

/**
 * Format an ISO timestamp as a relative time like "2m ago" or "3h ago".
 * Falls back to the absolute date for anything over a day.
 */
function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  // Older than a day — absolute date
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/**
 * VersionHistory drawer — slides in from the right, lists past snapshots
 * newest-first with a Revert button on each entry. Reverts are non-destructive:
 * restoring v5 when the latest is v9 writes a new v10 tagged "Reverted to v5".
 *
 * Props:
 *   integrationName — app to show history for
 *   onClose — called to dismiss the drawer
 *   onRestored(result) — called after a successful revert with the API response
 *                        ({ success, restoredFrom, newVersion, url, summary })
 * The newest entry is always marked as "Current" (it represents whatever's
 * currently deployed, since every deploy creates a snapshot).
 */
export default function VersionHistory({ integrationName, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringVersion, setRestoringVersion] = useState(null);

  const load = useCallback(async () => {
    if (!integrationName) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listVersions(integrationName);
      setVersions(data.versions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [integrationName]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape — common drawer convention
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleRevert = useCallback(async (version) => {
    if (!integrationName) return;
    const confirmMsg =
      `Revert to v${version}?\n\n` +
      `Your current state will be saved as a new version first, so nothing is lost. ` +
      `You can always move forward again from here.`;
    if (!window.confirm(confirmMsg)) return;

    setRestoringVersion(version);
    try {
      const result = await restoreVersion(integrationName, version);
      if (onRestored) onRestored(result);
      // Refresh the list so the new forward snapshot appears at the top
      await load();
    } catch (err) {
      setError(`Revert failed: ${err.message}`);
    } finally {
      setRestoringVersion(null);
    }
  }, [integrationName, onRestored, load]);

  return (
    <div className="vh-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vh-drawer">
        <div className="vh-header">
          <div>
            <h2>Version history</h2>
            <div className="vh-header-sub">
              {integrationName} · auto-saved on every deploy
            </div>
          </div>
          <button className="vh-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="vh-body">
          {loading && <div className="vh-loading">Loading versions…</div>}

          {!loading && error && (
            <div className="vh-error">{error}</div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className="vh-empty">
              No versions yet. Snapshots are created every time an edit deploys
              successfully — make a change and it'll show up here.
            </div>
          )}

          {!loading && !error && versions.map((v, idx) => {
            const isCurrent = idx === 0;
            const isRestoring = restoringVersion === v.v;
            const isRevert = v.triggeredBy === "revert";
            return (
              <div key={v.v} className={`vh-entry${isCurrent ? " vh-current" : ""}`}>
                <div className="vh-entry-top">
                  <span className="vh-version">v{v.v}</span>
                  <span className="vh-ts">{formatRelative(v.ts)}</span>
                  {isRevert && <span className="vh-badge vh-badge-revert">Revert</span>}
                </div>
                <div className="vh-summary">
                  {v.summary || <span style={{ color: "#71717a", fontStyle: "italic" }}>No summary</span>}
                </div>
                {v.userPrompt && (
                  <div className="vh-prompt" title={v.userPrompt}>
                    “{v.userPrompt}”
                  </div>
                )}
                <div className="vh-entry-actions">
                  {isCurrent ? (
                    <span className="vh-current-label">Current</span>
                  ) : (
                    <button
                      className="vh-revert-btn"
                      onClick={() => handleRevert(v.v)}
                      disabled={isRestoring || restoringVersion != null}
                    >
                      {isRestoring ? "Restoring…" : "Revert"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
